import { api } from './config';
import type { RansomwareGroup, RansomwareVictim, RansomwareAttack } from '@/lib/types/ransomware';

const MAX_RETRIES = 5;
const BASE_DELAY = 500;

async function retryableRequest<T>(requestFn: () => Promise<T>, retries = MAX_RETRIES): Promise<T> {
  try {
    return await requestFn();
  } catch (error) {
    if (retries === 0) throw error;
    
    // Wait before retrying, with exponential backoff
    const delay = BASE_DELAY * Math.pow(2, MAX_RETRIES - retries);
    await new Promise(resolve => setTimeout(resolve, delay));
    
    console.log(`Retrying request, ${retries} attempts remaining`);
    return retryableRequest(requestFn, retries - 1);
  }
}

// ransomware.live changed /v2/info from a nested shape to flat lowercase keys,
// and dropped the cyberattack count entirely. Both shapes are declared so the
// UI keeps working if the upstream ever reverts.
export interface RansomwareInfo {
  groups?: number;
  victims?: number;
  Cyberattacks?: {
    "Last Update RSS": string;
    "Last Update json": string;
    Number: number;
  };
  Groups?: {
    "Last Update": string;
    Numbers: number;
  };
  Victims?: {
    "Last Update CSV": string;
    "Last Update RSS": string;
    "Last Update json": string;
    Numbers: number;
  };
}

export async function getRansomwareInfo(): Promise<RansomwareInfo> {
  return retryableRequest(async () => {
    const response = await api.get('/api/ransom/info/');
    return response.data;
  });
}

export async function getRansomwareGroups(): Promise<RansomwareGroup[]> {
  return retryableRequest(async () => {
    const response = await api.get('/api/ransom/groups');
    return response.data;
  });
}

export async function getMonthlyVictims(year: number, month: number): Promise<RansomwareVictim[]> {
  return retryableRequest(async () => {
    const response = await api.get(`/api/ransom/victims/${year}/${month}`);
    return response.data;
  });
}

export async function getAllAttacks(): Promise<RansomwareAttack[]> {
  return retryableRequest(async () => {
    const response = await api.get('/api/ransom/attacks');
    return response.data;
  });
}

export async function searchVictim(query: string): Promise<RansomwareAttack[]> {
  return retryableRequest(async () => {
    const response = await api.get(`/api/ransom/victim/${encodeURIComponent(query)}`);
    
    // Ensure we always return an array
    if (!Array.isArray(response.data)) {
      console.warn('Victim search response is not an array:', response.data);
      return [];
    }

    // Log the response size
    console.debug(`Victim search returned ${response.data.length} results`);

    // Map and validate each result
    const mappedResults = response.data.map((rawResult: any): RansomwareAttack => {
      return {
        victim: rawResult.victim, // Keep original victim if present
        title: rawResult.post_title,
        group_name: rawResult.group_name,
        added: rawResult.discovered, // "Date Reported"
        date: rawResult.published,   // "Incident Date"
        country: rawResult.country,
        domain: rawResult.website,
        activity: rawResult.activity,
        summary: rawResult.description,
        url: rawResult.post_url,
        // infostealer_details can be mapped if available in rawResult
      };
    });

    // Debug: Log each mapped result before filtering
    mappedResults.forEach((mappedItem, index) => {
      console.debug(`Mapped item [${index}]: title='${mappedItem.title}', victim='${mappedItem.victim}'`);
    });

    // Filter results that don't have a primary identifier after mapping
    const validResults = mappedResults.filter(result => {
      const isInvalid = !result.victim && !result.title;
      if (isInvalid) {
        console.warn('[NEW CODE] Invalid victim result after mapping (missing/empty victim AND title):', { victim: result.victim, title: result.title, originalMappedResult: result });
        return false;
      }
      return true;
    });

    return validResults;
  });
}

export async function searchGroup(name: string): Promise<RansomwareGroup> {
  return retryableRequest(async () => {
    const response = await api.get(`/api/ransom/group/${encodeURIComponent(name)}`);
    return response.data;
  });
}

export async function getRecentAttacks(): Promise<RansomwareAttack[]> {
  return retryableRequest(async () => {
    const response = await api.get('/api/ransom/recentattacks');
    return response.data;
  });
}

export async function getGroupVictims(groupName: string): Promise<RansomwareAttack[]> {
  return retryableRequest(async () => {
    const response = await api.get(`/api/ransom/groupvictims/${encodeURIComponent(groupName)}`);
    // Ensure we always return an array
    return Array.isArray(response.data) ? response.data : [];
  });
}